import React, { useContext, useState } from "react";
import {
  DialogTitle,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  DialogContent,
  DialogActions,
  IconButton,
  Dialog,
  Box,
  Stack,
  List,
  ListItem,
  ListItemText,
  Menu,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { convertDateTimeToUserTimezone } from "../../../util/workingHour";
import { UserContext } from "../../../components/auth/auth-context";
import { StyledDialog, StyledDialogContent } from "../../../assets/css/styledComponent";
import AppointmentBooking from "../../../pages/business/appointmentBooking";
import CancelSchedule from "../bookingType/cancelSchedule";
import { useSelectedBusiness } from "../../../redux/store";
import { useCalendar } from "./calendarContext";

const BookItOrCreate = ({ triggerDialog }) => {
  const { myBookingTypes, setBookingDatas } = useCalendar();
  const [showCancelModal, setShowCancelModal] = useState(false); // Controls the cancel modal
  // We'll use selectedBookings consistently for cancellation data.
  const [selectedBookings, setSelectedBookings] = useState([]);
  const userContext = useContext(UserContext);
  const providerUser = userContext.providerUser;
  const selectedBusiness = useSelectedBusiness();

  // Separate state for each dialog
  const [isBookDialogOpen, setIsBookDialogOpen] = useState(false);
  const [isUpdateDialogOpen, setIsUpdateDialogOpen] = useState(false);

  // bookingData can be a single event or an array of events (grouped)
  const [bookingData, setBookingData] = useState(null);

  // State for selected options in booking dialog
  const [selectedBooking, setSelectedBooking] = useState("");
  const [selectedBookingDay, setSelectedBookingDay] = useState("");
  const [selectedBookingMonth, setSelectedBookingMonth] = useState("");
  const [selectedBookingType, setSelectedBookingType] = useState("");

  // State for group menu anchor in the grouped update dialog
  const [groupMenuAnchor, setGroupMenuAnchor] = useState(null);

  // Trigger dialog function (passed via ref from parent)
  const handleOpenDialog = (type, data, day, month) => {
    if (day) setSelectedBookingDay(day);
    if (month) setSelectedBookingMonth(month);
    setSelectedBookingType(type);

    if (type === "book") {
      setIsBookDialogOpen(true);
    } else if (type === "update") {
      setBookingData(data);
      setIsUpdateDialogOpen(true);
    }
  };

  const handleCloseBookDialog = () => {
    setIsBookDialogOpen(false);
  };

  const handleCloseUpdateDialog = () => {
    setIsUpdateDialogOpen(false);
    setBookingData(null);
  };

  const cancelModal = () => {
      setShowCancelModal(false);
      setIsUpdateDialogOpen(false);
    // Do not modify bookingData so the update dialog stays unchanged
  };

  // Expose handleOpenDialog to parent through the triggerDialog prop
  if (triggerDialog) {
    triggerDialog(handleOpenDialog);
  }

  const handleBookingChange = (event) => {
    setSelectedBooking(event.target.value);
  };
 

  // When canceling an individual attendee, set selectedBookings accordingly.
  const handleCancelIndividual = (booking) => {
    setSelectedBookings([booking]);
    setShowCancelModal(true);
  };

  // For group cancellation, use the entire group data.
  const handleCancelGroup = () => {
    setSelectedBookings(bookingData);
    setShowCancelModal(true);
  };

  return (
    <>
      {/* Booking Dialog */}
      <Dialog
        open={isBookDialogOpen}
        onClose={handleCloseBookDialog}
        fullWidth
        sx={{
          "& .MuiDialogContent-root": {
            overflowY: "auto",
            "&::-webkit-scrollbar": {
              width: "2px",
            },
          },
          "@media (max-width:600px)": {
            maxWidth: "100%",
          },
        }}
      >
        <StyledDialogContent>
          <FormControl margin="normal" sx={{ width: "270px", border: "none" }}>
            <InputLabel id="time-format-label" sx={{ marginBottom: "8px", paddingLeft: "25px" }}>
              Choose a booking type to schedule.
            </InputLabel>
            <Select
              value={selectedBooking}
              onChange={handleBookingChange}
              label="Select Booking Type"
              sx={{
                backgroundColor: "#fff",
                fontSize: "0.875rem",
                border: "none",
                paddingLeft: "25px",
                "& .MuiOutlinedInput-notchedOutline": { display: "none" },
                "& .MuiSelect-icon": {
                  color: "#000",
                  left: 20,
                  right: "unset",
                },
                "& .MuiSelect-select": {
                  paddingTop: "15px",
                  paddingBottom: "10px",
                },
              }}
            >
              <MenuItem value="">
                <em>Select Booking Type</em>
              </MenuItem>
              {myBookingTypes?.map((booking, index) => (
                <MenuItem key={index} value={booking}>
                  {booking.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {selectedBooking && (
            <AppointmentBooking
              selectedBookingData={selectedBooking}
              selectedBookingDay={selectedBookingDay}
              selectedBookingMonth={selectedBookingMonth}
              businessUrl={selectedBusiness?.businessBasic?.url}
            />
          )}
        </StyledDialogContent>
      </Dialog>

      {/* Update Dialog */}
      <StyledDialog open={isUpdateDialogOpen} onClose={handleCloseUpdateDialog} fullWidth maxWidth="md">
        {bookingData ? (
          Array.isArray(bookingData) ? (
            // Render grouped event update dialog
            <Box display="flex" flexDirection="column">
              <DialogTitle>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Typography variant="h6">
                    Manage Attendees for {bookingData[0].bookingType.title} at{" "}
                    {new Date(bookingData[0].startTime).toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </Typography>
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      setGroupMenuAnchor(e.currentTarget);
                    }}
                  >
                    <MoreVertIcon />
                  </IconButton>
                  <Menu
                    anchorEl={groupMenuAnchor}
                    open={Boolean(groupMenuAnchor)}
                    onClose={() => setGroupMenuAnchor(null)}
                    anchorOrigin={{
                      vertical: "top",
                      horizontal: "right",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "right",
                    }}
                  >
                    <MenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        setGroupMenuAnchor(null);
                        handleCancelGroup();
                      }}
                    >
                      Cancel Group Booking
                    </MenuItem>
                  </Menu>
                </Box>
                <IconButton
                  aria-label="close"
                  onClick={handleCloseUpdateDialog}
                  sx={{
                    position: "absolute",
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent dividers>
                <Stack spacing={2}>
                  {bookingData.map((bd, index) => (
                    <Box
                      key={index}
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{ borderBottom: "1px solid #ccc", pb: 1 }}
                    >
                      <Box>
                        <Typography variant="body1">
                          <strong>Name:</strong> {bd.event?.inviteeName || "N/A"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Email:</strong> {bd.event?.inviteeEmail || "N/A"}
                        </Typography>
                      </Box>
                      <IconButton edge="end" color="error" onClick={() => handleCancelIndividual(bd)}>
                        <CloseIcon />
                      </IconButton>
                    </Box>
                  ))}
                </Stack>
              </DialogContent> 
            </Box>
          ) : (
            // Render single booking update dialog (existing behavior)
            <Box display="flex" flexDirection="column">
              <DialogTitle>
                Manage Invitee
                <IconButton
                  aria-label="close"
                  onClick={handleCloseUpdateDialog}
                  sx={{
                    position: "absolute",
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent dividers>
                <Stack spacing={2}>
                  <Typography variant="body1">
                    <strong>Name:</strong> {bookingData.event?.inviteeName || "N/A"}
                  </Typography>
                  <Typography variant="body1">
                    <strong>Booking Type:</strong> {bookingData.bookingType.bookingTypeName || "N/A"}
                  </Typography>
                  <Typography variant="body1">
                    <strong>Time:</strong>{" "}
                    {convertDateTimeToUserTimezone(bookingData.time, providerUser.timeZone.timezone) || "N/A"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Email:</strong> {bookingData.inviteeEmail || "N/A"}
                  </Typography>
                  {bookingData.inviteePhoneNumbers && bookingData.inviteePhoneNumbers.length > 0 && (
                    <Typography variant="body2" color="text.secondary">
                      <strong>Phone:</strong>{" "}
                      {bookingData.inviteePhoneNumbers.map((phone, index) => (
                        <React.Fragment key={phone}>
                          <a href={`tel:${phone}`} style={{ textDecoration: "none", color: "inherit" }}>
                            {phone}
                          </a>
                          {index < bookingData.inviteePhoneNumbers.length - 1 ? ", " : ""}
                        </React.Fragment>
                      ))}
                    </Typography>
                  )}
                  <Typography variant="body1">
                    <strong>Provider:</strong> {bookingData.staffName || "N/A"}
                  </Typography>
                </Stack>
              </DialogContent>
              <DialogActions>
                <Button variant="outlined" color="error" onClick={() => {
                  setSelectedBookings([bookingData]);
                  setShowCancelModal(true);
                }} startIcon={<CloseIcon />}>
                  Cancel Booking
                </Button>
              </DialogActions>
            </Box>
          )
        ) : (
          <Typography>No bookings for this date.</Typography>
        )}
          </StyledDialog>
          {showCancelModal && (
              <CancelSchedule
              currentData={selectedBookings.map(b => (b.event ))}
              answersData={selectedBookings.map(b => (b.event ))}
              showCancelModal={showCancelModal}
              setShowCancelModal={cancelModal}
            />
          )}
      </>
  );
}; 
export default BookItOrCreate;  